interface ColorStopType {
  threshold: number;
  color: string;
}

const areaCategoryOptions = [
  { label: 'Delområden Sverige', value: '2' },
  { label: 'DeSO', value: '1' },
  { label: 'kv100', value: '3' },
];

const housingTypeOptions = [
  { label: 'Villor/radhus', value: 'house' },
  { label: 'Bostadsrätter', value: 'apt' },
  { label: '1 rok', value: 'oneroom' },
  { label: '2 rok', value: 'tworoom' },
  { label: '3 rok', value: 'threeroom' },
  { label: '4 rok', value: 'fourroom' },
  { label: '5 rok', value: 'fiveroom_plus' },
  { label: 'Totalt', value: 'total' },
];

const methodOptions = [
  { label: 'Kvadratmeterpris - medel', value: 'avg_recounted_psqm_n_best' },
  { label: 'Pris - medel', value: 'avg_recounted_price_n_best' },
  { label: 'Månadsavgift - medel', value: 'mean_yearly_fee_sales' },
  { label: 'Byggår - medel', value: 'sales_byear_mean' },
  { label: 'Boarea - medel', value: 'mean_area_sales_n_best' },
  { label: 'Antal försäljningar per år', value: 'number_of_sales_per_year' },
];

const DYNAMIC_COLOURS = [
  '#FFEDA0',
  '#FED976',
  '#FEB24C',
  '#FD8D3C',
  '#FC4E2A',
  '#E31A1C',
  '#BD0026',
  '#800026',
];

const DEFAULT_CENTER = { lat: 59.274472, lng: 17.944866 };
const DEFAULT_ZOOM = 13;

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const rgbToHex = (r: number, g: number, b: number) =>
  '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const generateColorRange = (
  minValue: number,
  maxValue: number,
  step: number,
  minColor: string,
  maxColor: string
): ColorStopType[] => {
  const range: ColorStopType[] = [];
  const totalSteps = (maxValue - minValue) / step;

  const minRGB = hexToRgb(minColor);
  const maxRGB = hexToRgb(maxColor);
  if (!minRGB || !maxRGB || !isFinite(totalSteps) || totalSteps <= 0)
    return range;

  for (let i = 0; i <= totalSteps; i++) {
    const valueRatio = i / totalSteps;
    const r = minRGB.r + (maxRGB.r - minRGB.r) * valueRatio;
    const g = minRGB.g + (maxRGB.g - minRGB.g) * valueRatio;
    const b = minRGB.b + (maxRGB.b - minRGB.b) * valueRatio;
    range.push({
      threshold: minValue + step * i,
      color: rgbToHex(Math.round(r), Math.round(g), Math.round(b)),
    });
  }
  return range;
};

const generateDynamicColorRange = (values: number[]): ColorStopType[] => {
  if (!values.length) return [];
  const maxDataValue = Math.max(...values);
  const minDataValue = Math.min(...values);
  const step = (maxDataValue - minDataValue) / (DYNAMIC_COLOURS.length - 1 || 1);

  return DYNAMIC_COLOURS.map((color, i) => ({
    threshold: minDataValue + i * step,
    color,
  }));
};

const getPolygonColor = (value: number, mapping: ColorStopType[]): string => {
  for (const stop of mapping) {
    if (value < stop.threshold) return stop.color;
  }
  const last = mapping[mapping.length - 1];
  if (last && value >= last.threshold) return last.color;
  return DYNAMIC_COLOURS[0];
};

export {
  areaCategoryOptions,
  housingTypeOptions,
  methodOptions,
  DYNAMIC_COLOURS,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  generateColorRange,
  generateDynamicColorRange,
  getPolygonColor,
};
export type { ColorStopType };
